import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Clock, Calendar, Globe, Settings, AlertCircle, Check, Zap, Upload, FileText, Users, Search, Filter, X, UserCheck } from 'lucide-react';
import { leadsListService } from '../lib/supabaseServices';

// LeadsListSelection component
export const LeadsListSelection = ({ data, onSelectLeadsList, onRemoveLeadsList, onShowImport, darkMode }) => {
  const [availableLeadsList, setAvailableLeadsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);

  useEffect(() => {
    loadLeadsLists();
  }, []);

  const loadLeadsLists = async () => {
    try {
      const lists = await leadsListService.getLeadsLists();
      setAvailableLeadsList(lists);
    } catch (error) {
      console.error('Failed to load leads lists:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredLists = availableLeadsList.filter(list => 
    !data.selectedLeadsLists.find(selected => selected.id === list.id) &&
    list.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Selected Leads Lists */}
      <div>
        <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'} mb-4`}>
          Selected Leads Lists
        </h3>
        
        {data.selectedLeadsLists.length === 0 ? (
          <div className={`border-2 border-dashed rounded p-8 text-center ${
            darkMode ? 'border-slate-700' : 'border-gray-300'
          }`}>
            <Users className={`w-12 h-12 mx-auto mb-4 ${darkMode ? 'text-slate-600' : 'text-gray-400'}`} />
            <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-gray-600'}`}>
              No leads lists selected yet
            </p>
            <p className={`text-xs mt-2 ${darkMode ? 'text-slate-500' : 'text-gray-500'}`}>
              Add existing lists or import new leads below
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {data.selectedLeadsLists.map(list => (
              <div key={list.id} className={`flex items-center justify-between p-3 rounded ${
                darkMode ? 'bg-slate-800 border border-slate-700' : 'bg-gray-50 border border-gray-200'
              }`}>
                <div className="flex items-center space-x-3">
                  <Users className={`w-5 h-5 ${darkMode ? 'text-accent' : 'text-accent'}`} />
                  <div>
                    <p className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      {list.name}
                    </p>
                    <p className={`text-xs ${darkMode ? 'text-slate-400' : 'text-gray-600'}`}>
                      {list.total_leads || 0} leads
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => onRemoveLeadsList(list.id)}
                  className={`p-1.5 rounded transition-colors ${
                    darkMode 
                      ? 'text-red-400 hover:bg-red-500/20' 
                      : 'text-red-500 hover:bg-red-50'
                  }`}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add Leads Lists */}
      <div>
        <h4 className={`text-sm font-medium ${darkMode ? 'text-slate-300' : 'text-gray-700'} mb-3`}>
          Add Leads Lists
        </h4>
        
        <div className="space-y-3">
          {/* Search and Select Existing Lists */}
          <div className="relative">
            <div className={`flex items-center border rounded ${
              darkMode 
                ? 'bg-slate-800 border-slate-600' 
                : 'bg-white border-gray-300'
            }`}>
              <Search className={`w-5 h-5 ml-3 ${darkMode ? 'text-slate-400' : 'text-gray-400'}`} />
              <input
                type="text"
                placeholder="Search existing lists..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onFocus={() => setShowDropdown(true)}
                className={`flex-1 px-3 py-2 bg-transparent focus:outline-none ${
                  darkMode ? 'text-white placeholder-slate-400' : 'text-gray-900 placeholder-gray-400'
                }`}
              />
            </div>
            
            {showDropdown && filteredLists.length > 0 && (
              <div className={`absolute z-10 w-full mt-1 border rounded shadow-lg max-h-48 overflow-auto ${
                darkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'
              }`}>
                {filteredLists.map(list => (
                  <button
                    key={list.id}
                    onClick={() => {
                      onSelectLeadsList(list);
                      setSearchTerm('');
                      setShowDropdown(false);
                    }}
                    className={`w-full text-left px-4 py-2 hover:bg-opacity-10 transition-colors ${
                      darkMode ? 'hover:bg-accent' : 'hover:bg-accent'
                    }`}
                  >
                    <p className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      {list.name}
                    </p>
                    <p className={`text-xs ${darkMode ? 'text-slate-400' : 'text-gray-600'}`}>
                      {list.total_leads || 0} leads
                    </p>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Import New Leads Button */}
          <button
            onClick={onShowImport}
            className={`w-full py-3 border-2 border-dashed rounded transition-colors flex items-center justify-center space-x-2 ${
              darkMode 
                ? 'border-border text-secondary hover:border-accent hover:text-accent'
                : 'border-gray-300 text-gray-500 hover:border-accent hover:text-accent'
            }`}
          >
            <Upload className="w-5 h-5" />
            <span>Import New Leads List</span>
          </button>
        </div>
      </div>
    </div>
  );
};

// AccountSelection component
export const AccountSelection = ({ data, accounts, userProfile, onToggleAccount, darkMode }) => {
  const selectedCount = data.selectedAccounts.length;
  const maxAccounts = userProfile?.subscription_tier === 'premium' ? 10 : 3;

  return (
    <div className="space-y-6">
      <div>
        <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'} mb-2`}>
          Select Instagram Accounts
        </h3>
        <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-gray-600'} mb-4`}>
          Choose which accounts will send messages for this campaign
        </p>
        
        <div className={`mb-4 p-3 rounded ${darkMode ? 'bg-slate-800' : 'bg-gray-50'}`}>
          <div className="flex items-center justify-between">
            <span className={`text-sm ${darkMode ? 'text-slate-300' : 'text-gray-700'}`}>
              Selected Accounts
            </span>
            <span className={`text-sm font-medium ${
              selectedCount >= maxAccounts 
                ? 'text-orange-500' 
                : darkMode ? 'text-accent' : 'text-accent'
            }`}>
              {selectedCount} / {maxAccounts}
            </span>
          </div>
        </div>
      </div>

      <div className="space-y-3">
        {accounts.length === 0 ? (
          <div className={`text-center py-8 border-2 border-dashed rounded ${
            darkMode ? 'border-slate-700' : 'border-gray-300'
          }`}>
            <UserCheck className={`w-12 h-12 mx-auto mb-4 ${darkMode ? 'text-slate-600' : 'text-gray-400'}`} />
            <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-gray-600'}`}>
              No Instagram accounts connected yet
            </p>
            <p className={`text-xs mt-2 ${darkMode ? 'text-slate-500' : 'text-gray-500'}`}>
              Connect accounts in the Accounts section first
            </p>
          </div>
        ) : (
          accounts.map(account => {
            const isSelected = data.selectedAccounts.includes(account.id);
            const isDisabled = !isSelected && selectedCount >= maxAccounts;
            
            return (
              <div
                key={account.id}
                onClick={() => !isDisabled && onToggleAccount(account.id)}
                className={`p-4 border rounded cursor-pointer transition-all ${
                  isSelected
                    ? darkMode
                      ? 'border-accent bg-accent/10'
                      : 'border-accent bg-accent/10'
                    : isDisabled
                    ? darkMode
                      ? 'border-slate-800 bg-slate-900/50 opacity-50 cursor-not-allowed'
                      : 'border-gray-200 bg-gray-50 opacity-50 cursor-not-allowed'
                    : darkMode
                    ? 'border-slate-700 hover:border-slate-600'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      darkMode ? 'bg-slate-700' : 'bg-gray-200'
                    }`}>
                      <UserCheck className="w-5 h-5" />
                    </div>
                    <div>
                      <p className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        @{account.username}
                      </p>
                      <p className={`text-xs ${darkMode ? 'text-slate-400' : 'text-gray-600'}`}>
                        {account.display_name || account.username}
                      </p>
                    </div>
                  </div>
                  <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                    isSelected
                      ? darkMode
                        ? 'border-accent bg-accent'
                        : 'border-accent bg-accent'
                      : darkMode
                      ? 'border-slate-600'
                      : 'border-gray-300'
                  }`}>
                    {isSelected && <Check className="w-4 h-4 text-white" />}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {selectedCount >= maxAccounts && (
        <div className={`p-4 rounded ${darkMode ? 'bg-orange-500/20' : 'bg-orange-50'}`}>
          <p className={`text-sm ${darkMode ? 'text-orange-400' : 'text-orange-600'}`}>
            You've reached the maximum number of accounts for your plan.
            Upgrade to add more accounts.
          </p>
        </div>
      )}
    </div>
  );
};

// SchedulingSettings component
export const SchedulingSettings = ({ data, onChange, darkMode }) => {
  return (
    <div className="space-y-6">
      <div>
        <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'} mb-4`}>
          Scheduling & Timing
        </h3>

        {/* Time Window */}
        <div className="space-y-4">
          <div>
            <label className={`block text-sm font-medium ${darkMode ? 'text-slate-300' : 'text-gray-700'} mb-2`}>
              Daily Time Window
            </label>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={`block text-xs ${darkMode ? 'text-slate-400' : 'text-gray-600'} mb-1`}>
                  Start Time
                </label>
                <input
                  type="time"
                  value={data.send_time_start}
                  onChange={(e) => onChange('send_time_start', e.target.value)}
                  className={`w-full px-3 py-2 border rounded focus:ring-2 focus:border-transparent ${
                    darkMode 
                      ? 'bg-surface border-border text-primary focus:ring-accent' 
                      : 'bg-white border-gray-300 text-gray-900 focus:ring-blue-500'
                  }`}
                />
              </div>
              <div>
                <label className={`block text-xs ${darkMode ? 'text-slate-400' : 'text-gray-600'} mb-1`}>
                  End Time
                </label>
                <input
                  type="time"
                  value={data.send_time_end}
                  onChange={(e) => onChange('send_time_end', e.target.value)}
                  className={`w-full px-3 py-2 border rounded focus:ring-2 focus:border-transparent ${
                    darkMode 
                      ? 'bg-surface border-border text-primary focus:ring-accent' 
                      : 'bg-white border-gray-300 text-gray-900 focus:ring-blue-500'
                  }`}
                />
              </div>
            </div>
          </div>

          {/* Timezone */}
          <div>
            <label className={`block text-sm font-medium ${darkMode ? 'text-slate-300' : 'text-gray-700'} mb-2`}>
              <Globe className="w-4 h-4 inline mr-2" />
              Timezone
            </label>
            <select
              value={data.timezone}
              onChange={(e) => onChange('timezone', e.target.value)}
              className={`w-full px-3 py-2 border rounded focus:ring-2 focus:border-transparent ${
                darkMode 
                  ? 'bg-surface border-border text-primary focus:ring-accent' 
                  : 'bg-white border-gray-300 text-gray-900 focus:ring-blue-500'
              }`}
            >
              <option value="UTC">UTC</option>
              <option value="America/New_York">Eastern Time</option>
              <option value="America/Chicago">Central Time</option>
              <option value="America/Denver">Mountain Time</option>
              <option value="America/Los_Angeles">Pacific Time</option>
              <option value="Europe/London">London</option>
              <option value="Europe/Paris">Paris</option>
              <option value="Asia/Tokyo">Tokyo</option>
              <option value="Australia/Sydney">Sydney</option>
            </select>
          </div>

          {/* Daily Message Limits */}
          <div>
            <label className={`block text-sm font-medium ${darkMode ? 'text-slate-300' : 'text-gray-700'} mb-2`}>
              Daily Message Range
            </label>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={`block text-xs ${darkMode ? 'text-slate-400' : 'text-gray-600'} mb-1`}>
                  Minimum
                </label>
                <input
                  type="number"
                  min="1"
                  max="50"
                  value={data.daily_message_min}
                  onChange={(e) => onChange('daily_message_min', parseInt(e.target.value))}
                  className={`w-full px-3 py-2 border rounded focus:ring-2 focus:border-transparent ${
                    darkMode 
                      ? 'bg-surface border-border text-primary focus:ring-accent' 
                      : 'bg-white border-gray-300 text-gray-900 focus:ring-blue-500'
                  }`}
                />
              </div>
              <div>
                <label className={`block text-xs ${darkMode ? 'text-slate-400' : 'text-gray-600'} mb-1`}>
                  Maximum
                </label>
                <input
                  type="number"
                  min="1"
                  max="50"
                  value={data.daily_message_max}
                  onChange={(e) => onChange('daily_message_max', parseInt(e.target.value))}
                  className={`w-full px-3 py-2 border rounded focus:ring-2 focus:border-transparent ${
                    darkMode 
                      ? 'bg-surface border-border text-primary focus:ring-accent' 
                      : 'bg-white border-gray-300 text-gray-900 focus:ring-blue-500'
                  }`}
                />
              </div>
            </div>
            <p className={`text-xs mt-2 ${darkMode ? 'text-slate-400' : 'text-gray-600'}`}>
              Instagram allows up to 50 messages per day. We'll vary the daily count within your range.
            </p>
          </div>

          {/* Exclusion Settings */}
          <div className="space-y-3">
            <label className={`block text-sm font-medium ${darkMode ? 'text-slate-300' : 'text-gray-700'}`}>
              Exclusion Rules
            </label>
            
            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={data.exclude_weekends}
                onChange={(e) => onChange('exclude_weekends', e.target.checked)}
                className={`rounded ${darkMode ? 'bg-slate-800 border-slate-600' : ''}`}
              />
              <span className={`text-sm ${darkMode ? 'text-slate-300' : 'text-gray-700'}`}>
                <Calendar className="w-4 h-4 inline mr-2" />
                Exclude weekends
              </span>
            </label>
            
            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={data.exclude_holidays}
                onChange={(e) => onChange('exclude_holidays', e.target.checked)}
                className={`rounded ${darkMode ? 'bg-slate-800 border-slate-600' : ''}`}
              />
              <span className={`text-sm ${darkMode ? 'text-slate-300' : 'text-gray-700'}`}>
                <Calendar className="w-4 h-4 inline mr-2" />
                Exclude major holidays
              </span>
            </label>
          </div>
        </div>
      </div>
    </div>
  );
};

// ReviewAndLaunch component
export const ReviewAndLaunch = ({ data, accounts, darkMode, isEdit = false }) => {
  const selectedAccountsData = accounts?.filter(acc => data.selectedAccounts?.includes(acc.id)) || [];
  const totalLeads = data.selectedLeadsLists?.reduce((sum, list) => sum + (list.total_leads || 0), 0) || 0;
  const messageCount = (data.messageSequences?.filter(s => s.enabled).length || 0) + 
                       (data.messageSequences?.[0]?.followups?.filter(f => f.enabled).length || 0);

  return (
    <div className="space-y-6">
      <div>
        <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'} mb-2`}>
          {isEdit ? 'Review Changes' : 'Review Campaign'}
        </h3>
        <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-gray-600'}`}>
          {isEdit ? 'Confirm your campaign updates' : 'Review your campaign settings before launching'}
        </p>
      </div>

      {/* Campaign Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Basic Info */}
        <div className={`p-4 rounded border ${darkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
          <h4 className={`text-sm font-semibold mb-3 ${darkMode ? 'text-accent' : 'text-accent'}`}>
            Campaign Details
          </h4>
          <div className="space-y-2">
            <div>
              <p className={`text-xs ${darkMode ? 'text-slate-400' : 'text-gray-600'}`}>Name</p>
              <p className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                {data.name || 'Untitled Campaign'}
              </p>
            </div>
            {data.description && (
              <div>
                <p className={`text-xs ${darkMode ? 'text-slate-400' : 'text-gray-600'}`}>Description</p>
                <p className={`text-sm ${darkMode ? 'text-slate-300' : 'text-gray-700'}`}>
                  {data.description}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Leads & Accounts */}
        <div className={`p-4 rounded border ${darkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
          <h4 className={`text-sm font-semibold mb-3 ${darkMode ? 'text-emerald-400' : 'text-green-600'}`}>
            Targeting
          </h4>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className={`text-sm ${darkMode ? 'text-slate-300' : 'text-gray-700'}`}>
                <Users className="w-4 h-4 inline mr-1" />
                Total Leads
              </span>
              <span className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                {totalLeads}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className={`text-sm ${darkMode ? 'text-slate-300' : 'text-gray-700'}`}>
                <UserCheck className="w-4 h-4 inline mr-1" />
                Accounts
              </span>
              <span className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                {selectedAccountsData.length}
              </span>
            </div>
          </div>
        </div>

        {/* Messages */}
        <div className={`p-4 rounded border ${darkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
          <h4 className={`text-sm font-semibold mb-3 ${darkMode ? 'text-accent' : 'text-purple-600'}`}>
            Messages
          </h4>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className={`text-sm ${darkMode ? 'text-slate-300' : 'text-gray-700'}`}>
                Sequences
              </span>
              <span className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                {data.messageSequences?.filter(s => s.enabled).length || 0}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className={`text-sm ${darkMode ? 'text-slate-300' : 'text-gray-700'}`}>
                Total Messages
              </span>
              <span className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                {messageCount}
              </span>
            </div>
          </div>
        </div>

        {/* Schedule */}
        <div className={`p-4 rounded border ${darkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
          <h4 className={`text-sm font-semibold mb-3 ${darkMode ? 'text-orange-400' : 'text-orange-600'}`}>
            Schedule
          </h4>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className={`text-sm ${darkMode ? 'text-slate-300' : 'text-gray-700'}`}>
                <Clock className="w-4 h-4 inline mr-1" />
                Time Window
              </span>
              <span className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                {data.send_time_start} - {data.send_time_end}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className={`text-sm ${darkMode ? 'text-slate-300' : 'text-gray-700'}`}>
                <Zap className="w-4 h-4 inline mr-1" />
                Daily Rate
              </span>
              <span className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                {data.daily_message_min}-{data.daily_message_max} msgs
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Warning Messages */}
      {(!data.name || !data.selectedLeadsLists?.length || !data.selectedAccounts?.length) && (
        <div className={`p-4 rounded flex items-start space-x-3 ${
          darkMode ? 'bg-red-500/20 border border-red-500/30' : 'bg-red-50 border border-red-200'
        }`}>
          <AlertCircle className={`w-5 h-5 mt-0.5 flex-shrink-0 ${darkMode ? 'text-red-400' : 'text-red-600'}`} />
          <div>
            <p className={`text-sm font-medium ${darkMode ? 'text-red-300' : 'text-red-700'}`}>
              Missing Required Information
            </p>
            <ul className={`text-xs mt-1 space-y-1 ${darkMode ? 'text-red-400' : 'text-red-600'}`}>
              {!data.name && <li>• Campaign name is required</li>}
              {!data.selectedLeadsLists?.length && <li>• At least one leads list must be selected</li>}
              {!data.selectedAccounts?.length && <li>• At least one Instagram account must be selected</li>}
            </ul>
          </div>
        </div>
      )}

      {/* Success Message */}
      {data.name && data.selectedLeadsLists?.length > 0 && data.selectedAccounts?.length > 0 && (
        <div className={`p-4 rounded flex items-start space-x-3 ${
          darkMode ? 'bg-green-500/20 border border-green-500/30' : 'bg-green-50 border border-green-200'
        }`}>
          <Check className={`w-5 h-5 mt-0.5 flex-shrink-0 ${darkMode ? 'text-green-400' : 'text-green-600'}`} />
          <div>
            <p className={`text-sm font-medium ${darkMode ? 'text-green-300' : 'text-green-700'}`}>
              {isEdit ? 'Ready to update campaign' : 'Campaign ready to launch'}
            </p>
            <p className={`text-xs mt-1 ${darkMode ? 'text-green-400' : 'text-green-600'}`}>
              {isEdit 
                ? 'Your changes will be applied immediately'
                : `Will start sending messages to ${totalLeads} leads using ${selectedAccountsData.length} account${selectedAccountsData.length > 1 ? 's' : ''}`
              }
            </p>
          </div>
        </div>
      )}
    </div>
  );
};